import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, breadcrumb } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { FAQ } from "@/components/FAQ";
import DevisForm from "@/components/DevisForm";
import { SectionTitle, DataTable } from "@/components/PageBlocks";
import { trackPhoneClick } from "@/lib/analytics";
import { ArrowRight, Check, Phone } from "lucide-react";
import { EVENEMENTS_CONTENT } from "@/content/evenements";
import { usePageContent } from "@/lib/pageContent";
import { pageImageUrl } from "@/content/pageImages";
import { Rich } from "@/components/Rich";


export default function Evenements() {
  const { lang, localePath, alternateUrls } = useI18n();
  const c = usePageContent("evenements", lang === "en" ? "en" : "fr", EVENEMENTS_CONTENT[lang === "en" ? "en" : "fr"]);
  const [imgs, setImgs] = useState({});

  useSEO({
    title: c.meta.title,
    description: c.meta.description,
    keywords: c.meta.keywords,
    lang,
    alternates: alternateUrls(),
    jsonLd: breadcrumb([
      { name: lang === "en" ? "Home" : "Accueil", url: "https://lentracterestaurant.fr/" },
      { name: lang === "en" ? "Events" : "Événements", url: "https://lentracterestaurant.fr/evenements" },
    ]),
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => setImgs(r.data || {})).catch(() => {});
  }, []);

  const img = (slot) => pageImageUrl(imgs, "evenements", slot);
  const HERO = img("hero");
  const SPACE_IMGS = [img("space_1"), img("space_2"), img("space_3"), img("space_4")];
  const CARD_IMGS = [
    img("card_mariage"),
    img("card_seminaire"),
    img("card_bapteme"),
    img("card_anniversaire"),
  ];
  const PLATE = img("plate");

  return (
    <div data-testid="events-page">
      {/* HERO */}
      <section className="relative min-h-[62vh] flex items-end overflow-hidden -mt-[148px] pt-[148px]">
        {HERO && <img src={HERO} alt={c.heroAlt} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/65" />
        <div className="relative z-10 max-w-[1500px] mx-auto w-full px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <p className="overline text-[#E6CFA1]">{c.overline}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] text-white font-light max-w-4xl">
            {c.h1}
          </h1>
          <Rich as="p" value={c.intro} className="mt-8 text-white/85 text-base md:text-lg max-w-3xl leading-relaxed" />
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#devis" data-testid="hero-cta-devis" className="btn-gold">
              {c.ctaDevis} <ArrowRight size={14} />
            </a>
            <a
              href="tel:+33182001867"
              data-testid="hero-cta-phone"
              onClick={() => trackPhoneClick("events_hero")}
              className="btn-ghost-light"
            >
              <Phone size={14} /> {c.ctaCall}
            </a>
          </div>
        </div>
      </section>

      {/* EN BREF */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1100px] mx-auto">
          <SectionTitle>{c.brief.title}</SectionTitle>
          <div className="mt-10">
            <DataTable caption={c.brief.caption} rows={c.brief.rows} testid="events-brief-table" />
          </div>
        </div>
      </section>

      {/* CARTES ÉVÉNEMENTS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1500px] mx-auto">
          <SectionTitle>{c.cardsTitle}</SectionTitle>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            {c.cards.map((card, i) => (
              <Link
                key={card.to}
                to={localePath(card.to)}
                data-testid={`event-card-${card.to.replace("/", "")}`}
                className="group block border border-[#D9D3C5] hover:border-[#B8956A] transition-colors duration-300"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  {CARD_IMGS[i] && (
                    <img
                      src={CARD_IMGS[i]}
                      alt={card.alt}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  )}
                </div>
                <div className="p-7 md:p-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#203240] font-light">{card.title}</h3>
                  <Rich as="p" value={card.body} className="mt-4 text-[#3D4A5A] leading-relaxed" />
                  <span className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[#B8956A]">
                    {card.cta} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NOS ESPACES */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1500px] mx-auto">
          <SectionTitle>{c.spaces.title}</SectionTitle>
          <div className="mt-12 space-y-16 md:space-y-24">
            {c.spaces.items.map((sp, i) => (
              <div
                key={sp.name}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center"
              >
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    {SPACE_IMGS[i] && (
                      <img src={SPACE_IMGS[i]} alt={sp.alt} className="w-full h-full object-cover img-soft" />
                    )}
                  </div>
                </div>
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <h3 className="font-display text-2xl md:text-3xl text-[#203240] font-light">{sp.name}</h3>
                  <Rich as="p" value={sp.body} className="mt-5 text-[#3D4A5A] leading-relaxed max-w-xl" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 md:mt-28 max-w-[900px]">
            <h3 className="font-display text-2xl md:text-3xl text-[#203240] font-light">{c.capacityTable.title}</h3>
            <div className="mt-8">
              <DataTable
                caption={c.capacityTable.caption}
                head={c.capacityTable.head}
                rows={c.capacityTable.rows}
                testid="events-capacity-table"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FORMULE CLÉ EN MAIN */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1100px] mx-auto">
          <SectionTitle>{c.formula.title}</SectionTitle>

          <p className="mt-10 text-[#B8956A] uppercase tracking-[0.22em] text-xs">{c.formula.includedTitle}</p>
          <ul className="mt-6 space-y-4">
            {c.formula.included.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
          <Rich as="p" value={c.formula.price} className="mt-8 text-[#203240] text-lg font-display" />

          <p className="mt-14 text-[#B8956A] uppercase tracking-[0.22em] text-xs">{c.formula.optionsTitle}</p>
          <ul className="mt-6 space-y-4">
            {c.formula.options.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>

          <p className="mt-14 text-[#B8956A] uppercase tracking-[0.22em] text-xs">{c.formula.othersTitle}</p>
          <ul className="mt-6 space-y-4">
            {c.formula.others.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CARTE ÉVÉNEMENTIELLE */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              {PLATE && <img src={PLATE} alt={c.carte.alt} className="w-full h-full object-cover img-soft" />}
            </div>
          </div>
          <div className="md:col-span-7">
            <SectionTitle>{c.carte.title}</SectionTitle>
            <Rich as="p" value={c.carte.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <dl className="mt-10 space-y-8">
              {c.carte.groups.map((g) => (
                <div key={g.label}>
                  <dt className="text-xs uppercase tracking-[0.22em] text-[#B8956A]">{g.label}</dt>
                  <Rich as="dd" value={g.body} className="mt-3 text-[#3D4A5A] leading-relaxed" />
                </div>
              ))}
            </dl>
            <p className="mt-10 italic text-[#697585]">{c.carte.note}</p>
            <Link to={localePath("/menu")} className="btn-ghost mt-10 inline-flex">
              {lang === "en" ? "See the menu" : "Voir la carte"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ORGANISATION */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.process.title}</SectionTitle>
          <ol className="mt-12 space-y-8" data-testid="events-process">
            {c.process.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-6">
                <span className="font-display text-3xl text-[#B8956A] leading-none w-10 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Rich as="p" value={s} className="text-[#3D4A5A] leading-relaxed pt-1" />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* BON À SAVOIR */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.goodToKnow.title}</SectionTitle>
          <ul className="mt-10 space-y-5">
            {c.goodToKnow.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ACCÈS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.access.title}</SectionTitle>
          <Rich as="p" value={c.access.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
          <ul className="mt-8 space-y-5">
            {c.access.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
          <Link to={localePath("/galerie")} className="btn-ghost mt-10 inline-flex">
            {lang === "en" ? "See the gallery" : "Voir la galerie"} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* AVIS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#203240]">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#F6F0E7] font-light">
            {c.reviews.title}
          </h2>
          <p className="mt-6 text-[#E6CFA1] tracking-wide">{c.reviews.ratings}</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            {c.reviews.quotes.map((q) => (
              <blockquote key={q.author} className="border-l border-[#B8956A] pl-6">
                <p className="font-display text-xl md:text-2xl text-[#F6F0E7] font-light leading-snug">
                  « {q.quote} »
                </p>
                <footer className="mt-5 text-sm text-[#F6F0E7]/60">— {q.author}</footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.faqTitle}</SectionTitle>
          <div className="mt-10">
            <FAQ items={c.faq} testid="events-faq" schemaId="ld-faq-evenements" />
          </div>
        </div>
      </section>

      {/* DEVIS */}
      <section id="devis" className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF] scroll-mt-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline">{c.overline}</p>
            <SectionTitle>{c.devis.title}</SectionTitle>
            <Rich as="p" value={c.devis.body} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <p className="mt-6 text-[#697585]">{c.devis.contact}</p>
          </div>
          <div className="md:col-span-7">
            <div className="border border-[#D9D3C5] bg-[#F6F0E7] p-7 md:p-10">
              <DevisForm pageSource="/evenements" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
